import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { splitExaminersLiveFirst, type PublicExaminer } from "./publicExaminers";

type PublicExaminerResponse = {
  examiners: PublicExaminer[];
};

export function usePublicExaminers(pollingMs = 30_000) {
  const [examiners, setExaminers] = useState<PublicExaminer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const hasData = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (hasData.current) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await api<PublicExaminerResponse>("/api/public/examiners");
      if (!mounted.current) return;
      setExaminers(data.examiners);
      setUpdatedAt(new Date());
      setError(null);
      hasData.current = true;
    } catch {
      if (mounted.current) setError("直播状态暂时无法更新，请稍后重试。");
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, pollingMs);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pollingMs, refresh]);

  const groups = useMemo(() => splitExaminersLiveFirst(examiners), [examiners]);

  return {
    ...groups,
    loading,
    refreshing,
    error,
    updatedAt,
    refresh
  };
}
